import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HistoryOverlay from './HistoryOverlay';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listChats } from '../api/adaFetch';
import { useAuth } from '../contexts/Auth';
import { TitleContext } from '../contexts/TitleContext';
import LegalHover from '../components/LegalHover';
import { useDesign } from './DesignContext';
import AdaMark from './AdaMark';
import AmbientSky from './ambient/AmbientSky';
import DesignPicker from './DesignPicker';
import { TOD_WINDOWS } from './ambient/palettes';
import {
  CONDITION_LABELS,
} from './ambient/weather';
import {
  DEFAULT_PLACE_ID,
  PLACE_STORAGE_KEY,
  PLACES,
  placeById,
  placeByLabel,
  toPlaceOverride,
} from './places';

// Sidebar always starts hidden — history lives behind the History button.
function readCollapsedPref() {
  return true;
}

export default function ChatAppShell({ children, onLogout }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { title } = useContext(TitleContext);
  const {
    todWindow,
    todLabel,
    condition,
    weather,
    ambient,
    location: place,
    setPlaceOverride,
  } = useDesign();
  const [chats, setChats] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsedPref);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(() => {
    try {
      return localStorage.getItem(PLACE_STORAGE_KEY) || DEFAULT_PLACE_ID;
    } catch {
      return DEFAULT_PLACE_ID;
    }
  });

  // Visual viewport follows the on-screen keyboard; the dock never changes parents.
  useEffect(() => {
    const viewport = window.visualViewport;
    const resize = () => document.documentElement.style.setProperty('--ada-viewport-height', `${viewport?.height || window.innerHeight}px`);
    resize();
    viewport?.addEventListener('resize', resize);
    window.addEventListener('resize', resize);
    return () => {
      viewport?.removeEventListener('resize', resize);
      window.removeEventListener('resize', resize);
      document.documentElement.style.removeProperty('--ada-viewport-height');
    };
  }, []);

  const activeChatId = location.pathname.startsWith('/chat/')
    ? location.pathname.split('/')[2]
    : null;

  const refreshChats = useCallback(async () => {
    if (!token) return;
    try {
      const data = await listChats(token);
      setChats(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load chats', err);
    }
  }, [token]);

  useEffect(() => {
    refreshChats();
  }, [refreshChats, location.pathname]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const match = placeByLabel(place?.city);
    if (match) setSelectedPlaceId(match.id);
  }, [place]);

  const closeOrCollapseSidebar = () => {
    setSidebarOpen(false);
    if (window.matchMedia('(min-width: 861px)').matches) {
      setSidebarCollapsed(true);
    }
  };

  const handlePlaceSelect = (next) => {
    setSelectedPlaceId(next.id);
    setPlaceOverride(toPlaceOverride(next));
    try {
      localStorage.setItem(PLACE_STORAGE_KEY, next.id);
    } catch {
      /* session-only */
    }
    setPickerOpen(false);
  };

  const headerTitle =
    location.pathname === '/' ? 'Ada' : title || 'Chat';

  const shellClass = [
    'ada-shell ada-chat-shell',
    sidebarOpen ? 'is-sidebar-open' : '',
    sidebarCollapsed ? 'is-sidebar-collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const cityLabel = place?.city || placeById(selectedPlaceId).label;
  const conditionLabel = CONDITION_LABELS[condition] || '';
  const cityChip = [
    cityLabel,
    weather.tempC != null && `${Math.round(weather.tempC)}°`,
    conditionLabel,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className={shellClass}
      style={{
        background:
          'linear-gradient(180deg, var(--ada-sky-top), var(--ada-sky-mid) 52%, var(--ada-sky-bottom))',
      }}
    >
      <AmbientSky />
      <div className="ada-shell__frost" aria-hidden />
      <div
        className="ada-sidebar-backdrop"
        onClick={() => setSidebarOpen(false)}
        aria-hidden
      />
      <aside className="ada-sidebar">
        <div className="ada-sidebar__top">
          <LegalHover>
            <h1 className="ada-brand"><AdaMark />Ada</h1>
          </LegalHover>
          <button
            type="button"
            className="ada-icon-btn"
            onClick={closeOrCollapseSidebar}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <ChevronLeftRoundedIcon fontSize="small" />
          </button>
        </div>
        <button
          type="button"
          className="ada-new-chat"
          onClick={() => navigate('/')}
        >
          <AddRoundedIcon fontSize="small" style={{ marginRight: 8 }} />
          New chat
        </button>
        <ul className="ada-chat-list">
          {chats.map((chat) => (
            <li key={chat.id}>
              <button
                type="button"
                className={`ada-chat-item${
                  chat.id === activeChatId ? ' is-active' : ''
                }`}
                onClick={() => navigate(`/chat/${chat.id}`)}
              >
                <span className="ada-chat-item__name">{chat.name}</span>
                <span
                  className={`ada-chat-item__star${
                    chat.is_starred ? ' is-on' : ''
                  }`}
                >
                  <StarRoundedIcon sx={{ fontSize: 16 }} />
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="ada-sidebar__footer">
          <span
            className="ada-system-chip"
            title="Ada follows your place and local time"
          >
            <span className="ada-system-chip__dot" aria-hidden="true" />
            <span>Ada system</span>
          </span>
          <div className="ada-login__city-wrap">
            <button
              type="button"
              className="ada-location-chip"
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
              onClick={() => setPickerOpen((v) => !v)}
              title="Change city"
            >
              {cityChip || 'Locating…'}
            </button>
            {pickerOpen && (
              <div
                className="ada-city-picker"
                role="listbox"
                aria-label="Choose city"
              >
                {PLACES.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={item.id === selectedPlaceId}
                    className={`ada-city-picker__item${
                      item.id === selectedPlaceId ? ' is-active' : ''
                    }`}
                    onClick={() => handlePlaceSelect(item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ada-ambient-chip" title="Ambient sky — time & weather">
            <span>
              {ambientSummary({
                todWindow,
                todLabel,
                condition,
                weather,
                ambient,
              })}
            </span>
          </div>
          <button
            type="button"
            className="ada-logout-link"
            onClick={() => navigate('/worlds')}
          >
            Worlds
          </button>
          <button
            type="button"
            className="ada-logout-link"
            onClick={() => navigate('/connectors')}
          >
            Connectors
          </button>
          <button
            type="button"
            className="ada-logout-link"
            onClick={() => navigate('/settings')}
          >
            Settings
          </button>
          <button
            type="button"
            className="ada-logout-link"
            onClick={onLogout}
          >
            Log out
          </button>
        </div>
      </aside>

      <section className="ada-main">
        <header className="ada-main__header">
          <button type="button" className="ada-icon-btn" aria-label="Open sidebar" title="Chats and settings" onClick={() => { setSidebarCollapsed(false); setSidebarOpen(true); }}><MenuRoundedIcon fontSize="small" /></button>
          <button
            type="button"
            className="ada-icon-btn ada-main__menu"
            onClick={() => navigate('/')}
            aria-label="New chat"
            title="New chat"
          >
            <EditRoundedIcon fontSize="small" />
          </button>
          <h2 className="ada-main__title"><AdaMark />{headerTitle}</h2>
          <DesignPicker />
        </header>
        <div className="ada-main__body">{children}</div>
      </section>

      <HistoryOverlay open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}

/** Sidebar chip copy: "Morning · 18°C Clear" (or "Ambient off"). */
function ambientSummary({ todWindow, todLabel, condition, weather, ambient }) {
  if (!ambient) return 'Ambient off';
  const tod =
    todLabel || TOD_WINDOWS.find((w) => w.id === todWindow)?.label || todWindow;
  const wx = [
    weather.tempC != null && `${Math.round(weather.tempC)}°C`,
    CONDITION_LABELS[condition] || '',
  ]
    .filter(Boolean)
    .join(' ');
  return wx ? `${tod} · ${wx}` : tod;
}
